import { TZDate } from '@date-fns/tz';

// Format timezone for display
export const formatTimezone = (tz: string) => {
  try {
    if (tz === 'UTC') {
      return 'Universal Time Coordinated (UTC)';
    }
    return tz.split('/').map(part => part.replace('_', ' ')).join(' - ');
  } catch (e) {
    return tz;
  }
};

// Mapping of time zones to their standard and daylight-saving abbreviations
const timeZoneAbbreviations: { [key: string]: { standard: string; daylight: string } } = {
  'UTC': { standard: 'UTC', daylight: 'UTC' },
  'Africa/Abidjan': { standard: 'GMT', daylight: 'GMT' },
  'Africa/Accra': { standard: 'GMT', daylight: 'GMT' },
  'Africa/Addis_Ababa': { standard: 'EAT', daylight: 'EAT' },
  'Africa/Algiers': { standard: 'CET', daylight: 'CET' },
  'Africa/Cairo': { standard: 'EET', daylight: 'EEST' },
  'Africa/Casablanca': { standard: 'WET', daylight: 'WEST' },
  'Africa/Johannesburg': { standard: 'SAST', daylight: 'SAST' },
  'Africa/Lagos': { standard: 'WAT', daylight: 'WAT' },
  'Africa/Nairobi': { standard: 'EAT', daylight: 'EAT' },
  'Africa/Tunis': { standard: 'CET', daylight: 'CET' },
  'America/Anchorage': { standard: 'AKST', daylight: 'AKDT' },
  'America/Argentina/Buenos_Aires': { standard: 'ART', daylight: 'ART' },
  'America/Bogota': { standard: 'COT', daylight: 'COT' },
  'America/Caracas': { standard: 'VET', daylight: 'VET' },
  'America/Chicago': { standard: 'CST', daylight: 'CDT' },
  'America/Denver': { standard: 'MST', daylight: 'MDT' },
  'America/Edmonton': { standard: 'MST', daylight: 'MDT' },
  'America/Halifax': { standard: 'AST', daylight: 'ADT' },
  'America/Lima': { standard: 'PET', daylight: 'PET' },
  'America/Los_Angeles': { standard: 'PST', daylight: 'PDT' },
  'America/Mexico_City': { standard: 'CST', daylight: 'CDT' },
  'America/Montreal': { standard: 'EST', daylight: 'EDT' },
  'America/New_York': { standard: 'EST', daylight: 'EDT' },
  'America/Phoenix': { standard: 'MST', daylight: 'MST' }, // No DST
  'America/Santiago': { standard: 'CLT', daylight: 'CLST' },
  'America/Sao_Paulo': { standard: 'BRT', daylight: 'BRST' },
  'America/St_Johns': { standard: 'NST', daylight: 'NDT' },
  'America/Toronto': { standard: 'EST', daylight: 'EDT' },
  'America/Vancouver': { standard: 'PST', daylight: 'PDT' },
  'Asia/Almaty': { standard: 'ALMT', daylight: 'ALMT' },
  'Asia/Baghdad': { standard: 'AST', daylight: 'AST' },
  'Asia/Baku': { standard: 'AZT', daylight: 'AZST' },
  'Asia/Bangkok': { standard: 'ICT', daylight: 'ICT' },
  'Asia/Beirut': { standard: 'EET', daylight: 'EEST' },
  'Asia/Dhaka': { standard: 'BST', daylight: 'BST' },
  'Asia/Dubai': { standard: 'GST', daylight: 'GST' },
  'Asia/Ho_Chi_Minh': { standard: 'ICT', daylight: 'ICT' },
  'Asia/Hong_Kong': { standard: 'HKT', daylight: 'HKT' },
  'Asia/Jakarta': { standard: 'WIB', daylight: 'WIB' },
  'Asia/Jerusalem': { standard: 'IST', daylight: 'IDT' },
  'Asia/Karachi': { standard: 'PKT', daylight: 'PKT' },
  'Asia/Kolkata': { standard: 'IST', daylight: 'IST' },
  'Asia/Kuwait': { standard: 'AST', daylight: 'AST' },
  'Asia/Manila': { standard: 'PHT', daylight: 'PHT' },
  'Asia/Qatar': { standard: 'AST', daylight: 'AST' },
  'Asia/Riyadh': { standard: 'AST', daylight: 'AST' },
  'Asia/Seoul': { standard: 'KST', daylight: 'KST' },
  'Asia/Shanghai': { standard: 'CST', daylight: 'CST' },
  'Asia/Singapore': { standard: 'SGT', daylight: 'SGT' },
  'Asia/Taipei': { standard: 'CST', daylight: 'CST' },
  'Asia/Tashkent': { standard: 'UZT', daylight: 'UZT' },
  'Asia/Tehran': { standard: 'IRST', daylight: 'IRDT' },
  'Asia/Tokyo': { standard: 'JST', daylight: 'JST' },
  'Atlantic/Reykjavik': { standard: 'GMT', daylight: 'GMT' },
  'Australia/Adelaide': { standard: 'ACST', daylight: 'ACDT' },
  'Australia/Brisbane': { standard: 'AEST', daylight: 'AEST' }, // No DST
  'Australia/Darwin': { standard: 'ACST', daylight: 'ACST' }, // No DST
  'Australia/Hobart': { standard: 'AEST', daylight: 'AEDT' },
  'Australia/Melbourne': { standard: 'AEST', daylight: 'AEDT' },
  'Australia/Perth': { standard: 'AWST', daylight: 'AWST' }, // No DST
  'Australia/Sydney': { standard: 'AEST', daylight: 'AEDT' },
  'Europe/Amsterdam': { standard: 'CET', daylight: 'CEST' },
  'Europe/Athens': { standard: 'EET', daylight: 'EEST' },
  'Europe/Belgrade': { standard: 'CET', daylight: 'CEST' },
  'Europe/Berlin': { standard: 'CET', daylight: 'CEST' },
  'Europe/Brussels': { standard: 'CET', daylight: 'CEST' },
  'Europe/Bucharest': { standard: 'EET', daylight: 'EEST' },
  'Europe/Budapest': { standard: 'CET', daylight: 'CEST' },
  'Europe/Copenhagen': { standard: 'CET', daylight: 'CEST' },
  'Europe/Dublin': { standard: 'GMT', daylight: 'IST' },
  'Europe/Helsinki': { standard: 'EET', daylight: 'EEST' },
  'Europe/Istanbul': { standard: 'TRT', daylight: 'TRT' },
  'Europe/Kiev': { standard: 'EET', daylight: 'EEST' },
  'Europe/Lisbon': { standard: 'WET', daylight: 'WEST' },
  'Europe/London': { standard: 'GMT', daylight: 'BST' },
  'Europe/Luxembourg': { standard: 'CET', daylight: 'CEST' },
  'Europe/Madrid': { standard: 'CET', daylight: 'CEST' },
  'Europe/Moscow': { standard: 'MSK', daylight: 'MSK' },
  'Europe/Oslo': { standard: 'CET', daylight: 'CEST' },
  'Europe/Paris': { standard: 'CET', daylight: 'CEST' },
  'Europe/Prague': { standard: 'CET', daylight: 'CEST' },
  'Europe/Rome': { standard: 'CET', daylight: 'CEST' },
  'Europe/Stockholm': { standard: 'CET', daylight: 'CEST' },
  'Europe/Vienna': { standard: 'CET', daylight: 'CEST' },
  'Europe/Warsaw': { standard: 'CET', daylight: 'CEST' },
  'Europe/Zurich': { standard: 'CET', daylight: 'CEST' },
  'Indian/Maldives': { standard: 'MVT', daylight: 'MVT' },
  'Pacific/Auckland': { standard: 'NZST', daylight: 'NZDT' },
  'Pacific/Fiji': { standard: 'FJT', daylight: 'FJST' },
  'Pacific/Guam': { standard: 'ChST', daylight: 'ChST' },
  'Pacific/Honolulu': { standard: 'HST', daylight: 'HST' },
  'Pacific/Port_Moresby': { standard: 'PGT', daylight: 'PGT' },
  'Pacific/Samoa': { standard: 'SST', daylight: 'SDT' },
};

// Check if DST is active for a given time zone and date
export const isDSTActive = (timeZone: string, date: Date): boolean => {
  const jan = new Date(date.getFullYear(), 0, 1);
  const jul = new Date(date.getFullYear(), 6, 1);

  const janOffset = new Date(jan.toLocaleString('en-US', { timeZone })).getTimezoneOffset();
  const julOffset = new Date(jul.toLocaleString('en-US', { timeZone })).getTimezoneOffset();
  const currentOffset = new Date(date.toLocaleString('en-US', { timeZone })).getTimezoneOffset();

  // The standard (non-DST) offset is the larger (less negative/more positive) value.
  const standardOffset = Math.max(janOffset, julOffset);
  return currentOffset < standardOffset;
};

// Get time zone abbreviation based on the mapping and DST status, using TZDate for calculation.
export const getTimezoneAbbr = (date: Date, timeZone: string): string => {
  const abbreviations = timeZoneAbbreviations[timeZone];
  if (!abbreviations) {
    // Return offset using the TZDate-offset
    const offset = -date.getTimezoneOffset() / 60;
    const sign = offset >= 0 ? '+' : '';
    return `GMT${sign}${offset}`;
  }

  const year = date.getFullYear();
  // Construct TZDate instances for January 1 and July 1 in the event's timezone.
  const jan = new TZDate(`${year}-01-01T00:00:00Z`, timeZone);
  const jul = new TZDate(`${year}-07-01T00:00:00Z`, timeZone);
  // Use the larger (less negative) offset as the standard (non-DST) offset.
  const standardOffset = Math.max(jan.getTimezoneOffset(), jul.getTimezoneOffset());
  const currentOffset = date.getTimezoneOffset();
  const isDST = currentOffset < standardOffset;
  return isDST ? abbreviations.daylight : abbreviations.standard;
};

// Full list of IANA timezones
export const TIMEZONES = [
  'UTC',
  'Africa/Abidjan',
  'Africa/Accra',
  'Africa/Addis_Ababa',
  'Africa/Algiers',
  'Africa/Cairo',
  'Africa/Casablanca',
  'Africa/Johannesburg',
  'Africa/Lagos',
  'Africa/Nairobi',
  'Africa/Tunis',
  'America/Anchorage',
  'America/Argentina/Buenos_Aires',
  'America/Bogota',
  'America/Caracas',
  'America/Chicago',
  'America/Denver',
  'America/Edmonton',
  'America/Halifax',
  'America/Lima',
  'America/Los_Angeles',
  'America/Mexico_City',
  'America/Montreal',
  'America/New_York',
  'America/Phoenix',
  'America/Santiago',
  'America/Sao_Paulo',
  'America/St_Johns',
  'America/Toronto',
  'America/Vancouver',
  'Asia/Almaty',
  'Asia/Baghdad',
  'Asia/Baku',
  'Asia/Bangkok',
  'Asia/Beirut',
  'Asia/Dhaka',
  'Asia/Dubai',
  'Asia/Ho_Chi_Minh',
  'Asia/Hong_Kong',
  'Asia/Jakarta',
  'Asia/Jerusalem',
  'Asia/Karachi',
  'Asia/Kolkata',
  'Asia/Kuwait',
  'Asia/Manila',
  'Asia/Qatar',
  'Asia/Riyadh',
  'Asia/Seoul',
  'Asia/Shanghai',
  'Asia/Singapore',
  'Asia/Taipei',
  'Asia/Tashkent',
  'Asia/Tehran',
  'Asia/Tokyo',
  'Atlantic/Reykjavik',
  'Australia/Adelaide',
  'Australia/Brisbane',
  'Australia/Darwin',
  'Australia/Hobart',
  'Australia/Melbourne',
  'Australia/Perth',
  'Australia/Sydney',
  'Europe/Amsterdam',
  'Europe/Athens',
  'Europe/Belgrade',
  'Europe/Berlin',
  'Europe/Brussels',
  'Europe/Bucharest',
  'Europe/Budapest',
  'Europe/Copenhagen',
  'Europe/Dublin',
  'Europe/Helsinki',
  'Europe/Istanbul',
  'Europe/Kiev',
  'Europe/Lisbon',
  'Europe/London',
  'Europe/Luxembourg',
  'Europe/Madrid',
  'Europe/Moscow',
  'Europe/Oslo',
  'Europe/Paris',
  'Europe/Prague',
  'Europe/Rome',
  'Europe/Stockholm',
  'Europe/Vienna',
  'Europe/Warsaw',
  'Europe/Zurich',
  'Indian/Maldives',
  'Pacific/Auckland',
  'Pacific/Fiji',
  'Pacific/Guam',
  'Pacific/Honolulu',
  'Pacific/Port_Moresby',
  'Pacific/Samoa'
].map(tz => ({
  id: tz,
  text: formatTimezone(tz)
}));